<template>
  <div class="order-menu-container">
    <!-- 店铺信息区域 -->
    <div class="shop-info">
      <div class="shop-name">
        <el-icon><Shop /></el-icon>
        <span>{{ tableStore.tenantName }}</span>
      </div>
      <div class="shop-status">在线点单：{{ cartStore.onlineCount }}人</div>
    </div>

    <!-- 顶部搜索和定位 -->
    <div class="top-bar">
      <div class="location">
        <el-icon><Location /></el-icon>
        <span class="location-text">{{ tableStore.tableNo }}桌・{{ tableStore.customerCnt }}人</span>
      </div>
      <div class="search-box">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索店内商品"
          clearableName
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
      </div>
      <div>
        <el-icon @click="toOrderDetail()"><Document  /></el-icon>
      </div>
    </div>

    <!-- 分类和菜品区域 -->
    <div class="menu-section">
      <!-- 左侧分类导航 -->
      <div class="category-sidebar">
        <div 
          v-for="category in menuStore.categoryList" 
          :key="category.categoryId"
          class="category-item"
          :class="{ 'active': activeCategory === category.categoryId }"
          @click="selectCategory(category.categoryId)"
        >
          <span class="category-icon" v-if="category.icon">
            <el-icon><component :is="category.icon" /></el-icon>
          </span>
          <span class="category-name">{{ category.name }}</span>
          <span class="category-count" v-if="cartStore.getDishCountByCategory(category.categoryId) > 0">
            {{ cartStore.getDishCountByCategory(category.categoryId) }}
          </span>
        </div>
      </div>

      <!-- 右侧菜品列表 -->
      <div class="dish-list-container">
        <div 
          v-for="category in menuStore.categoryList" 
          :key="category.categoryId"
          class="dish-category-section"
          :id="'category-' + category.categoryId"
        >
          <div class="category-header">
            <h4 class="category-title">{{ category.name }}</h4>
            <span class="category-desc">{{ category.description }}</span>
          </div>
          
          <div class="dish-list">
            <div 
              v-for="dish in getDishesByCategory(category.categoryId)" 
              :key="dish.dishId" 
              class="dish-item"
            >
              <div class="dish-image-container">
                <DishImage :fileId="dish.fileId" :alt="dish.name" class="dish-image" @click="openDetailDialog(dish)"></DishImage>
                <div class="dish-tags" v-if="dish.tag">
                  <span class="dish-tag" :class="menuStore.getTagClass(dish.tagName)">
                    {{ dish.tagName }}
                  </span>
                </div>
              </div>
              <div class="dish-info">
                <div class="dish-name">{{ dish.name }}</div>
                <div class="dish-desc">{{ dish.description }}</div>
                <div class="dish-sales-info" style="display:none;">
                  <span class="monthly-sales">月售{{ dish.monthlySales || 0 }}</span>
                </div>
                <div class="dish-footer">
                  <div class="price-section">
                    <span class="current-price">¥{{ dish.currentPrice.toFixed(1)}}</span>
                    <span class="original-price" v-if="dish.currentPrice !== dish.price">
                      ¥{{ dish.price.toFixed(1) }}
                    </span>
                  </div>
                  <div class="action-section">
                    <el-button 
                      v-if="cartStore.getDishCount(dish.dishId) > 0"
                      size="small" 
                      @click.stop="updateCart(dish, -1)"
                      :icon="Minus" 
                      circle 
                    />
                    <span class="quantity" v-if="cartStore.getDishCount(dish.dishId) > 0">
                      {{ cartStore.getDishCount(dish.dishId) }}
                    </span>
                    <el-button 
                      type="primary" 
                      size="small" 
                      @click.stop="updateCart(dish, 1)"
                      :icon="cartStore.getDishCount(dish.dishId) > 0 ? Plus : ShoppingCart" 
                      circle 
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="shopping-cart" @click="toShopCart">
      <el-badge :value="cartStore.totalCount" :max="99" :hidden="cartStore.totalCount === 0">
        <el-icon size="24"><ShoppingCart /></el-icon>
      </el-badge>
    </div>
  </div>

  <!-- 菜品详情对话框 -->
  <DishDetailDialog
    v-model="detailDialogVisible"
    :dish="selectedDish"
    @addToCart="addToCartFromComponent"
  />

  
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { 
  Location, Search, Plus, 
  Minus, ShoppingCart, Document , Shop 
} from '@element-plus/icons-vue'
import { useCartStore } from '@/stores/common/cartStore'
import { useTableStore } from '@/stores/common/tableStore'
import { useMenuStore } from '@/stores/common/menuStore'
import { useRouter} from 'vue-router'
import DishDetailDialog from '@/components/DishDetailDialog'
import { ElMessage } from 'element-plus'
import DishImage from '@/components/DishImage.vue'
import socketService from '@/utils/socketService'

const cartStore = useCartStore()
const tableStore = useTableStore()
const menuStore = useMenuStore()

const router = useRouter()
const activeCategory = ref(menuStore.categoryList[0]?.categoryId)
const searchKeyword = ref('')
const dishListForShow = computed(() => {
  return menuStore.dispalyDishList.filter(dish => searchKeyword.value === '' || dish.name.includes(searchKeyword.value))
});

const detailDialogVisible = ref(false)
const selectedDish = ref(null)

const openDetailDialog = (dish) => {
  selectedDish.value = dish
  detailDialogVisible.value = true
}

// 获取分类下的菜品
const getDishesByCategory = (categoryId) => {
  return dishListForShow.value.filter(dish => dish.categoryId === categoryId || dish.tag === categoryId)
}

// 选择分类
const selectCategory = (categoryId) => {
  activeCategory.value = categoryId
  const element = document.getElementById(`category-${categoryId}`)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

const addToCartFromComponent = (data) => {
  updateCart(data.dish, data.quantity)
}

// 打开购物车对话框
const toShopCart = () => {
  router.push('/CartConfirm')
}

const toOrderDetail = () => {
  router.push('/orderComfirm')
}

const updateCart = (dish, quantity) => {
  socketService.emit('cart-updated', {
    dishId : dish.dishId
    ,name : dish.name
    ,categoryId : dish.categoryId
    ,currentPrice : dish.currentPrice
    ,fileId : dish.fileId
    ,quantity : quantity
  })
}

onMounted( async () => {
  if(!tableStore.tableNo){
    router.push('/forbidden')
    ElMessage.error('请重新扫码')
    return
  }

  await menuStore.getCategoryList();
  await menuStore.getDishList();
})
</script>

<style scoped>
.order-menu-container {
  height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
}

/* 顶部栏样式 */
.top-bar {
  background: white;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.location {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #333;
  font-weight: 500;
  cursor: pointer;
}

.location .el-icon {
  color: #ff6b6b;
}

.arrow {
  font-size: 12px;
  margin-left: 2px;
}

.search-box {
  flex: 1;
}

.tag {
  font-size: 12px;
  color: #666;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
}

.rank-1 {
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
}

.rank-2 {
  background: linear-gradient(135deg, #ff8e53 0%, #ffb86b 100%);
}

.rank-3 {
  background: linear-gradient(135deg, #ffb86b 0%, #ffd166 100%);
}

.rank-other {
  background: #ccc;
}

/* 分类和菜品区域 */
.menu-section {
  flex: 1;
  display: flex;
  overflow: hidden;
  background: white;
}

/* 左侧分类导航 */
.category-sidebar {
  width: 90px;
  background: #f8f8f8;
  overflow-y: auto;
  flex-shrink: 0;
}

.category-item {
  padding: 16px 8px;
  text-align: center;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;
  border-left: 3px solid transparent;
}

.category-item:hover {
  background: #f0f0f0;
}

.category-item.active {
  background: white;
  border-left: 3px solid #ff6b6b;
  font-weight: 600;
  color: #ff6b6b;
}

.category-item.active .category-name {
  color: #ff6b6b;
}

.category-icon {
  display: block;
  margin-bottom: 6px;
}

.category-icon .el-icon {
  font-size: 20px;
}

.category-name {
  font-size: 13px;
  color: #666;
  display: block;
}

.category-count {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #ff6b6b;
  color: white;
  font-size: 10px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 右侧菜品列表 */
.dish-list-container {
  flex: 1;
  overflow-y: auto;
  padding: 0 8px 8px;
}

.dish-category-section {
  margin-bottom: 32px;
}

.category-header {
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.category-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.category-desc {
  font-size: 12px;
  color: #999;
}

/* 菜品项样式 */
.dish-list {
  display: grid;
  gap: 16px;
}

.dish-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s;
}

.dish-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.dish-image-container {
  position: relative;
  flex-shrink: 0;
}

.dish-image {
  width: 100px;
  height: 100px;
  border-radius: 4px;
  object-fit: cover;
}

.dish-tags {
  position: absolute;
  top: 0px;
  left: 0px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dish-tag {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  color: white;
  white-space: nowrap;
}

.tag-signature {
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
}
.tag-hot {
  background: linear-gradient(135deg, #ff8e53 0%, #ffb86b 100%);
}
.tag-discount {
  background: linear-gradient(135deg, #4cd964 0%, #5ac8fa 100%);
}
.tag-spicy {
  background: linear-gradient(135deg, #ff2d55 0%, #ff6b6b 100%);
}
.tag-default {
  background: rgba(0, 0, 0, 0.6);
}

.dish-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.dish-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.dish-desc {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.dish-sales-info {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.monthly-sales {
  font-size: 12px;
  color: #999;
}

.dish-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
}

.price-section {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.current-price {
  font-size: 18px;
  color: #ff6b6b;
  font-weight: bold;
}

.original-price {
  font-size: 13px;
  color: #999;
  text-decoration: line-through;
}

.action-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quantity {
  min-width: 20px;
  text-align: center;
  font-weight: 500;
  font-size: 14px;
}

/* 滚动条样式 */
.category-sidebar::-webkit-scrollbar,
.dish-list-container::-webkit-scrollbar {
  width: 4px;
}

.category-sidebar::-webkit-scrollbar-track,
.dish-list-container::-webkit-scrollbar-track {
  background: transparent;
}

.category-sidebar::-webkit-scrollbar-thumb,
.dish-list-container::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 2px;
}

.category-sidebar::-webkit-scrollbar-thumb:hover,
.dish-list-container::-webkit-scrollbar-thumb:hover {
  background: #ccc;
}

.shopping-cart {
  position: fixed;
  left: 20px;
  bottom: 20px;
  z-index: 1000;
  background: #409EFF;
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: transform 0.2s;
}

.shopping-cart:hover {
  transform: scale(1.1);
}

.shop-info {
  background: #2a2e35;  /* 深灰底，比原色更柔和 */
  color: #e8eef2;
  border-bottom: 1px solid #3e444c;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  flex-shrink: 0;
}

.shop-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 500;
  color: #ffaa66;  /* 暖橙色，替代原金黄 */
}

.shop-name .el-icon {
  color: #ff884d;  /* 图标稍深，增加层次 */
}

.shop-status {
  font-size: 12px;
  background: rgba(255, 170, 102, 0.2);  /* 半透明暖橙 */
  color: #ffaa66;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 12px;
  border: none;
}

/* 响应式调整 */
@media (max-width: 380px) {
  
  .dish-image {
    width: 80px;
    height: 80px;
  }
}
</style>